/**
 * Reading an ADR-142 capability-gap envelope for the operator — ADR-148 D2.
 *
 * The envelope already carries, exactly and completely, what a waiting case needs from a
 * human: `artifacts[]` with an `owner`, and a `proposed_shape` naming the thing to create.
 * The defect ADR-148 was written for is not missing data. It is a surface that read ONE
 * field (`resume_trigger.match.mcp_server`), found it empty, and reported the absence of
 * its own expectation as a property of the case:
 *
 *     waiting on a capability with no named server — it cannot wake on its own
 *
 * That sentence was shown for a case waiting on a single well-defined file edit. It reads
 * as *malformed and unresolvable*, which is worse than saying nothing — an operator who
 * believes a case is broken does not go and fix it.
 *
 * Lives in utils rather than in the page so the list view and the detail view cannot
 * describe the same case differently, and so the wording is testable without a DOM.
 */

/** One required change and who owns it. `owner` is core | fleet | operator (ADR-142 D8 —
 *  never the emitting agent). Typed loosely because it arrives as an untyped envelope. */
export interface GapArtifact {
  path_or_thing: string
  owner: string
  note?: string
}

interface GapEnvelope {
  invalid?: boolean
  artifacts?: unknown
  proposed_shape?: { kind?: string; detail?: string; mcp_server?: string; command_name?: string }
  workaround?: { exists?: boolean; detail?: string }
}

interface ResumeTrigger {
  kind?: string
  match?: Record<string, string>
}

/** The case fields this module reads. A structural subset of DiagnosticInvestigation so a
 *  test can build one in three lines. */
export interface GapCaseView {
  kind?: string
  status?: string
  gap?: Record<string, unknown> | null
  resume_trigger?: Record<string, unknown> | null
}

/**
 * The one status that means "this case is blocked, waiting for a capability to arrive".
 *
 * A case in any other state is not waiting on evidence: `active` means it RESUMED and a
 * specialist is authoring, `awaiting-approval` means the decision is the blocker, and the
 * terminal three are over. The resume trigger stays on the record for all of them, because
 * it is history — which is why reading it unconditionally is wrong.
 */
const AWAITING_EVIDENCE = 'awaiting-evidence'

function envelope(it: GapCaseView): GapEnvelope {
  return (it.gap ?? {}) as GapEnvelope
}

/** ADR-064 D4's allowlist file, used only when the gap did not name it. Named here rather
 *  than inlined so the fallback is visible: if an envelope names the path, we prefer the
 *  envelope, because the path is per-fleet and this constant will eventually be wrong. */
const DEFAULT_ALLOWLIST_PATH = 'toolchain/activity-commands.yaml'

/**
 * Every artifact the OPERATOR owns.
 *
 * This is the subset that turns "blocked" into something a person can do this afternoon.
 * `core` and `fleet` artifacts are somebody else's queue; showing all three equally is how
 * the one actionable line gets lost among two that are not.
 */
export function operatorArtifacts(it: GapCaseView): GapArtifact[] {
  const raw = envelope(it).artifacts
  if (!Array.isArray(raw)) return []
  return raw
    .filter((a): a is GapArtifact => !!a && typeof a === 'object' && (a as GapArtifact).owner === 'operator')
    .filter(a => typeof a.path_or_thing === 'string' && a.path_or_thing.trim() !== '')
}

/** The allowlist file this gap names, falling back to the conventional path. */
function allowlistPath(it: GapCaseView): string {
  const named = operatorArtifacts(it).find(a => a.path_or_thing.includes('activity-commands'))
  return named ? named.path_or_thing : DEFAULT_ALLOWLIST_PATH
}

/**
 * What a waiting capability gap is waiting FOR, in the operator's terms.
 *
 * Reads the ADR-142 D4 resume trigger, so it names the thing that will actually wake the
 * case rather than a generic "waiting" — and, per ADR-148 D2, it describes the operator's
 * ACTION for the shapes that have one instead of describing the mechanism's disappointment.
 *
 * Returns null when there is nothing honest to say (a diagnosis case, a trigger of another
 * kind); the caller renders nothing rather than a placeholder.
 */
export function waitingOn(it: GapCaseView): string | null {
  if (it.kind !== 'capability_gap') return null
  if (envelope(it).invalid) return 'report could not be filed — nothing is waiting on it'
  // Only a case that is ACTUALLY waiting may say what it is waiting for.
  //
  // Case 9bc8109d on spartan read "waiting for `google_drive` to be granted to analytics"
  // while it was `active`, had resumed five times, and had been dispatched to a builder
  // three times — google_drive was mounted the whole time, which is precisely WHY it had
  // resumed. The trigger is a durable field on the record, so rendering it regardless of
  // status made a case that was being worked advertise a blocker it had already cleared.
  //
  // A status this function does not recognise is treated as not-waiting: silence is
  // recoverable, and a confident false blocker sends someone to grant a capability that
  // is already there.
  if ((it.status ?? '') !== AWAITING_EVIDENCE) return null
  const trigger = (it.resume_trigger ?? {}) as ResumeTrigger
  if (trigger.kind !== 'capability_available') return null
  const m = trigger.match ?? {}
  const agent = m.agent || 'this agent'

  if (m.mcp_server) return `waiting for \`${m.mcp_server}\` to be granted to ${agent}`

  // ADR-148 D1's second evidence shape. This case is one file edit away from resuming, and
  // it used to render as if it were unresolvable.
  if (m.command_name) {
    return `waiting for the \`${m.command_name}\` entry in \`${allowlistPath(it)}\` (then deploy)`
  }

  // Neither shape named: the trigger genuinely cannot wake this case, and D7's deadline
  // owns it. Still say what a human could do — the envelope usually knows, even when the
  // trigger does not.
  const mine = operatorArtifacts(it)
  if (mine.length > 0) {
    const paths = mine.map(a => `\`${a.path_or_thing}\``).join(', ')
    return `waiting on an operator change: ${paths} — it escalates at the deadline rather than waking on its own`
  }
  return 'waiting on a capability that names no server or command — it escalates at the deadline rather than waking on its own'
}
